import axiosClient from '../interceptors';
import {ApiRoutes} from "../routes/ApiRoutes";
import {getAPIUrl} from "./BaseService";

export interface BillingPlanLimits {
    student_limit: number | null;
    classroom_limit: number | null;
    teacher_limit: number | null;
    ai_feedback_monthly_limit: number | null;
}

export interface BillingPlanFeatures {
    batch_ai: boolean;
    bulk_scores: boolean;
    batch_reports: boolean;
    csv_import_export: boolean;
    email_reports: boolean;
    whatsapp_reports: boolean;
    guardian_portal: boolean;
    custom_ai_prompts: boolean;
    school_logo: boolean;
    auto_promotion: boolean;
    full_analytics: boolean;
    multi_teacher: boolean;
    clean_pdf: boolean;
    send_invite: string[];
}

export interface BillingPlan {
    key: string;
    name: string;
    description: string;
    monthly_price_cents: number;
    annual_price_cents: number;
    billing_model?: 'flat' | 'per_seat';
    min_seats?: number | null;
    price_per_seat_cents?: number | null;
    limits: BillingPlanLimits;
    features?: BillingPlanFeatures;
    ai_detail_levels: string[];
}

export interface BillingSubscription {
    plan: string;
    plan_name?: string | null;
    status: string;
    /**
     * App-gate state derived server-side: ``active`` and ``founder_active``
     * grant access; ``pending_payment_method``, ``waitlisted``, ``past_due``,
     * ``cancelled`` block it. Drives post-payment polling — we wait for this
     * to flip to a granting value before showing success.
     */
    access_state?: string | null;
    trial_plan?: string | null;
    trial_ends_at?: string | null;
    current_period_end?: string | null;
    ai_addon?: boolean;
    ai_feedback_count?: number | null;
    ai_feedback_reset_date?: string | null;
    ai_detail_levels?: string[] | null;
    limits?: BillingPlanLimits | null;
    features?: BillingPlanFeatures | null;
    student_count?: number | null;
    classroom_count?: number | null;
    teacher_count?: number | null;
    staff_count?: number | null;
    purchased_seats?: number | null;
    has_portal_access?: boolean;
}

export interface PaddleClientConfig {
    client_token: string;
    environment: string;
    beta_mode?: boolean;
}

export interface FreemiusClientConfig {
    product_id: string;
    public_key: string;
    environment: string;
}

/**
 * Provider-agnostic checkout client config returned from
 * ``GET /billing/client-config``. ``config`` shape depends on ``provider``.
 */
export interface BillingClientConfig {
    provider: 'paddle' | 'freemius';
    config: PaddleClientConfig | FreemiusClientConfig;
    beta_mode?: boolean;
}

export interface BillingCheckoutSession {
    checkout_url: string;
    transaction_id: string;
    /** Identifies which provider produced this session. */
    provider?: 'paddle' | 'freemius';
    /** Freemius only: options to pass directly to ``FS.Checkout.open()``. */
    overlay_options?: Record<string, unknown> | null;
}

interface BillingPortalSession {
    portal_url: string;
}

export async function getBillingPlans(): Promise<BillingPlan[]> {
    const url = getAPIUrl() + ApiRoutes.BILLING_PLANS;
    const response = await axiosClient.get<BillingPlan[]>(url);
    return response.data;
}

export async function getBillingSubscription(schoolId: string): Promise<BillingSubscription> {
    const url = `${getAPIUrl()}${ApiRoutes.BILLING_BASE}/${schoolId}/subscription`;
    const response = await axiosClient.get<BillingSubscription>(url);
    return response.data;
}

export async function getPaddleClientConfig(): Promise<PaddleClientConfig> {
    const url = `${getAPIUrl()}${ApiRoutes.BILLING_BASE}/paddle/config`;
    const response = await axiosClient.get<PaddleClientConfig>(url);
    return response.data;
}

/**
 * Provider-aware client config. Prefer this over ``getPaddleClientConfig`` so
 * the frontend can branch on ``provider`` and load the right SDK.
 */
export async function getBillingClientConfig(): Promise<BillingClientConfig> {
    const url = `${getAPIUrl()}${ApiRoutes.BILLING_BASE}/client-config`;
    const response = await axiosClient.get<BillingClientConfig>(url);
    return response.data;
}

export interface SetupIntentResponse {
    checkout_url: string;
    transaction_id: string;
    plan: string;
    billing_cycle: string | null;
    provider?: 'paddle' | 'freemius';
    overlay_options?: Record<string, unknown> | null;
}

export async function createSetupIntent(): Promise<SetupIntentResponse> {
    const url = `${getAPIUrl()}${ApiRoutes.BILLING_BASE}/setup-intent`;
    const response = await axiosClient.post<SetupIntentResponse>(url);
    return response.data;
}

export interface AttachPaymentMethodResponse {
    access_state: string;
    plan: string;
    payment_method_attached_at: string | null;
    next_invoice_scheduled_for: string | null;
    /**
     * ``"active"`` when the provider confirmed a card and the school was
     * flipped to ``founder_active``. ``"pending_confirmation"`` (HTTP 202)
     * when the provider hasn't yet exposed the payment method — the FE
     * should keep polling and the webhook will activate the school.
     */
    status?: 'active' | 'pending_confirmation';
}

export async function attachPaymentMethod(
    transactionId?: string,
): Promise<AttachPaymentMethodResponse> {
    const url = `${getAPIUrl()}${ApiRoutes.BILLING_BASE}/payment-methods`;
    const response = await axiosClient.post<AttachPaymentMethodResponse>(
        url,
        transactionId ? { transaction_id: transactionId } : {},
    );
    return response.data;
}

export async function createBillingCheckout(
    schoolId: string,
    plan: string,
    interval: 'monthly' | 'annual' = 'monthly',
    successUrl?: string,
    licenses?: number,
): Promise<BillingCheckoutSession> {
    const url = `${getAPIUrl()}${ApiRoutes.BILLING_BASE}/${schoolId}/checkout`;
    const payload: Record<string, unknown> = {
        plan,
        interval,
        success_url: successUrl,
    };
    if (licenses != null) {
        payload.licenses = licenses;
    }
    const response = await axiosClient.post<BillingCheckoutSession>(url, payload);
    return response.data;
}

export async function getBillingPortalUrl(schoolId: string): Promise<string> {
    const url = `${getAPIUrl()}${ApiRoutes.BILLING_BASE}/${schoolId}/portal`;
    const response = await axiosClient.post<BillingPortalSession>(url);
    return response.data.portal_url;
}
